import { useStakedAmount } from '@/helpers/solana/useStakedAmount';
import useValidator from '@/helpers/useValidator';
import { useDataStore } from '@/store/data';
import { computed, Ref, ref } from 'vue';

export default function useStaking(amount: Ref<number>) {
  const dataStore = useDataStore();
  const staked = useStakedAmount();
  const { data: validator, isLoading } = useValidator();

  const currentStakedAmount = computed(() => staked.stakedAmount.value);

  // Validator data
  const apy = computed(() => {
    return validator?.value?.totalApy;
  });
  const stakingApy = computed(() => validator?.value?.stakingApy);
  const jitoApy = computed(() => validator?.value?.jitoApy);
  const stakeCommission = computed(() => validator?.value?.stakeCommission);
  const mevCommission = computed(() => validator?.value?.mevCommission);

  const newPositionEarnings = computed(() => {
    const stakingEarnings = amount.value * (stakingApy.value ?? 0);
    const mevEarnings = amount.value * (jitoApy.value ?? 0);
    return stakingEarnings + mevEarnings;
  });

  const newAmountStacked = computed(() => {
    return (currentStakedAmount.value ?? 0) + amount.value;
  });

  // Position yield
  const positionStakeEarnings = computed(
    () => newAmountStacked.value * (stakingApy.value ?? 0),
  );
  const positionMevEarnings = computed(
    () => newAmountStacked.value * (jitoApy.value ?? 0),
  );
  const totalUserEarnings = computed(() => {
    return positionStakeEarnings.value + positionMevEarnings.value;
  });
  const positionAPY = computed(() => {
    const { totalAPYBeforeCommission } = getAPYBeforeCommission({
      stakingAPYAfterCommission: stakingApy.value ?? 0,
      mevAPYAfterCommission: jitoApy.value ?? 0,
      stakingCommission: stakeCommission.value ?? 0,
      mevCommission: mevCommission.value ?? 0,
    });
    return totalAPYBeforeCommission;
  });

  // SonarWatch earnings
  const sonarwatchEarnings = computed(() =>
    calculateValidatorEarnings({
      stakedAmount: newAmountStacked.value,
      stakingAPYAfterCommission: stakingApy.value ?? 0,
      mevAPYAfterCommission: jitoApy.value ?? 0,
      stakingCommission: stakeCommission.value ?? 0,
      mevCommission: mevCommission.value ?? 0,
    }),
  );

  const isReady = computed(() => !isLoading.value);

  return {
    isReady,
    currentStakedAmount,
    newAmountStacked,
    newPositionEarnings: newPositionEarnings,
    positionAPY: positionAPY,
    userEarnings: {
      stakeEarnings: positionStakeEarnings,
      mevEarnings: positionMevEarnings,
      totalEarnings: totalUserEarnings,
      userAPY: apy,
    },
    sonarwatchEarnings,
    comissions: {
      stake: stakeCommission,
      mev: mevCommission,
    },
  };
}

function getAPYBeforeCommission({
  stakingAPYAfterCommission,
  mevAPYAfterCommission,
  stakingCommission,
  mevCommission,
}: {
  stakingAPYAfterCommission: number;
  mevAPYAfterCommission: number;
  stakingCommission: number;
  mevCommission: number;
}) {
  const stakingAPYBeforeCommission =
    stakingAPYAfterCommission / (1 - stakingCommission);
  const mevAPYBeforeCommission = mevAPYAfterCommission / (1 - mevCommission);
  return {
    stakingAPYBeforeCommission,
    mevAPYBeforeCommission,
    totalAPYBeforeCommission:
      stakingAPYBeforeCommission + mevAPYBeforeCommission,
  };
}

function calculateValidatorEarnings(props: {
  stakedAmount: number;
  stakingAPYAfterCommission: number;
  mevAPYAfterCommission: number;
  stakingCommission: number;
  mevCommission: number;
}) {
  const {
    stakedAmount,
    stakingAPYAfterCommission,
    mevAPYAfterCommission,
    stakingCommission,
    mevCommission,
  } = props;

  // Retrieve APY before commission
  const { stakingAPYBeforeCommission, mevAPYBeforeCommission } =
    getAPYBeforeCommission({
      stakingAPYAfterCommission,
      mevAPYAfterCommission,
      stakingCommission,
      mevCommission,
    });

  // Calculate validator earnings
  const validatorStakingEarnings =
    stakedAmount * stakingAPYBeforeCommission * stakingCommission;
  const validatorMevEarnings =
    stakedAmount * mevAPYBeforeCommission * mevCommission;

  const validatorTotalEarnings =
    validatorStakingEarnings + validatorMevEarnings;

  return validatorTotalEarnings;
}
